<template>
  <div class="pv-chat">
    <header class="pv-chat__head">
      <div>
        <div class="pv-chat__eyebrow">
          <span class="pv-chat__eyebrow-mark" />
          <span>Design Previews</span>
        </div>
        <h2 class="pv-chat__title">Ask AI to tweak</h2>
        <div class="pv-chat__subtitle">{{ preview.name }}</div>
      </div>
      <button class="pv-chat__close" title="Close" @click="$emit('close')">
        <Icon name="chevron-right" :size="18" />
      </button>
    </header>

    <div ref="scroll" class="pv-chat__messages">
      <div v-if="!messages.length" class="pv-chat__intro">
        <p>
          Tell me what to change on the live site — I'll edit the files and
          publish a new version (you can always roll back).
        </p>
        <p class="pv-chat__intro-hint">
          e.g. “Make the header sticky and change the CTA button to turquoise.”
        </p>
      </div>

      <div
        v-for="(m, i) in messages"
        :key="i"
        class="pv-chat__msg"
        :class="`pv-chat__msg--${m.role}`"
      >
        <div class="pv-chat__msg-role">
          {{ m.role === "user" ? "You" : "Alice" }}
        </div>
        <div class="pv-chat__msg-body">{{ m.content }}</div>
        <div v-if="m.version" class="pv-chat__published">
          <Icon name="external" :size="12" />
          <span
            >Published v{{ m.version }} —
            <a :href="url" target="_blank" rel="noopener">view live</a> (takes
            up to a minute to refresh)</span
          >
        </div>
        <div v-if="m.blocked" class="pv-chat__blocked">
          The change was NOT published: {{ m.blocked.summary }}
        </div>
      </div>

      <div v-if="sending" class="pv-chat__msg pv-chat__msg--assistant">
        <div class="pv-chat__msg-role">Alice</div>
        <div class="pv-chat__msg-body">
          <ThinkingDots :size="6" />
          <div class="pv-chat__working">
            Editing the site files — this can take a few minutes…
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="pv-chat__error">{{ error }}</div>

    <form class="pv-chat__input" @submit.prevent="send">
      <textarea
        v-model="input"
        class="pv-chat__textarea"
        rows="2"
        placeholder="Describe the tweak…"
        :disabled="sending"
        @keydown.enter.exact.prevent="send"
      />
      <button
        type="submit"
        class="pv-chat__send"
        :disabled="sending || !input.trim()"
      >
        <Icon name="arrow-right" :size="16" />
      </button>
    </form>
  </div>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";
import Icon from "@/components/alice/Icon.vue";
import ThinkingDots from "@/components/alice/ThinkingDots.vue";

export default {
  name: "PreviewTweakChat",
  components: { Icon, ThinkingDots },
  props: {
    preview: { type: Object, required: true },
  },
  emits: ["close", "published"],
  data() {
    return {
      messages: [],
      input: "",
      sending: false,
      error: "",
    };
  },
  computed: {
    url() {
      return (
        this.preview.url ||
        `https://valsplat-previews.web.app/${this.preview.slug}/`
      );
    },
  },
  methods: {
    async send() {
      const text = this.input.trim();
      if (!text || this.sending) return;
      this.error = "";
      this.sending = true;
      this.messages.push({ role: "user", content: text });
      this.input = "";
      this.scrollDown();
      try {
        const call = httpsCallable(functions, "tweakPreview", {
          timeout: 540000,
        });
        const res = await call({
          slug: this.preview.slug,
          messages: this.messages.map((m) => ({
            role: m.role,
            content: m.content,
          })),
        });
        const data = res.data || {};
        this.messages.push({
          role: "assistant",
          content: data.text || "",
          version: data.version || null,
          blocked: data.blocked || null,
        });
        if (data.version) this.$emit("published", data);
      } catch (e) {
        this.error =
          (e && e.message) || "Something went wrong. Please try again.";
      } finally {
        this.sending = false;
        this.scrollDown();
      }
    },
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.scroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.pv-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--vp-white);
  border-left: 1px solid var(--border-hairline);
}
.pv-chat__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 20px 16px;
  border-bottom: 1px solid var(--border-hairline);
}
.pv-chat__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.pv-chat__eyebrow-mark {
  width: 8px;
  height: 8px;
  background: var(--accent-100);
}
.pv-chat__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--vp-black);
}
.pv-chat__subtitle {
  margin-top: 2px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.pv-chat__close {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--fg-3);
  padding: 4px;
}
.pv-chat__close:hover {
  color: var(--vp-black);
}

.pv-chat__messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.pv-chat__intro {
  color: var(--fg-2);
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.55;
}
.pv-chat__intro-hint {
  margin-top: 10px;
  color: var(--fg-3);
  font-style: italic;
}

.pv-chat__msg-role {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 6px;
}
.pv-chat__msg-body {
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.6;
  color: var(--vp-black);
  white-space: pre-wrap;
  word-break: break-word;
}
.pv-chat__msg--user .pv-chat__msg-body {
  background: var(--bg-muted);
  padding: 10px 12px;
}
.pv-chat__working {
  margin-top: 8px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.pv-chat__published {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 6px 10px;
  background: var(--accent-20);
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--vp-black);
}
.pv-chat__published a {
  color: inherit;
}
.pv-chat__blocked {
  margin-top: 10px;
  padding: 6px 10px;
  background: #fdecec;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--vp-rood-100, #c62828);
}

.pv-chat__error {
  padding: 10px 20px;
  color: var(--vp-rood-100);
  font-family: var(--font-ui);
  font-size: 13px;
}

.pv-chat__input {
  display: flex;
  gap: 10px;
  padding: 16px 20px 20px;
  border-top: 1px solid var(--border-hairline);
}
.pv-chat__textarea {
  flex: 1;
  resize: none;
  border: 1px solid var(--border-hairline);
  outline: none;
  padding: 10px 12px;
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.5;
  color: var(--vp-black);
}
.pv-chat__textarea:focus {
  border-color: var(--vp-black);
}
.pv-chat__send {
  flex-shrink: 0;
  width: 44px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pv-chat__send:disabled {
  opacity: 0.4;
  cursor: default;
}
</style>
